import { Link } from "react-router-dom";

/**
 * Sehr schlanker Markdown-Renderer für Mikrobis Antworten:
 * Absätze, Aufzählungen, **fett**, [Text](/pfad)-Links sowie
 * "nackte" interne Pfade, URLs und E-Mail-Adressen.
 */
const INTERNAL_PATH =
  "\\/(?:en\\/)?(?:blog|beschwerden|termin|faq|e-book|bakteriophagen-therapie|phagentherapie|darmsanierung-wien)(?:\\/[a-zA-Z0-9\\-_]+)*";

const PATTERN = new RegExp(
  [
    "\\[([^\\]]+)\\]\\(([^)\\s]+)\\)", // 1,2 markdown link
    "\\*\\*([^*]+)\\*\\*", // 3 bold
    "(https?:\\/\\/[^\\s)<>\"']+)", // 4 url
    `(${INTERNAL_PATH})`, // 5 internal path
    "([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,})", // 6 email
  ].join("|"),
  "g",
);

const linkClass = "font-medium text-primary underline underline-offset-2";

const trimTrailing = (s: string) => s.replace(/[.,;:!?)\]]+$/, "");

export const MikrobiMessage = ({
  content,
  onNavigate,
}: {
  content: string;
  onNavigate?: () => void;
}) => {
  const renderInline = (text: string, keyPrefix: string) => {
    const nodes: React.ReactNode[] = [];
    PATTERN.lastIndex = 0;
    let last = 0;
    let match: RegExpExecArray | null;
    let i = 0;

    const internalLink = (href: string, label: string, key: string) => (
      <Link key={key} to={href} onClick={onNavigate} className={linkClass}>
        {label}
      </Link>
    );
    const externalLink = (href: string, label: string, key: string) => (
      <a
        key={key}
        href={href}
        target={href.startsWith("mailto:") || href.startsWith("tel:") ? undefined : "_blank"}
        rel="noopener noreferrer"
        className={linkClass}
      >
        {label}
      </a>
    );

    while ((match = PATTERN.exec(text)) !== null) {
      if (match.index > last) nodes.push(text.slice(last, match.index));
      const key = `${keyPrefix}-${i}`;
      let consumed = match[0].length;

      if (match[1] && match[2]) {
        const href = match[2];
        nodes.push(
          href.startsWith("/")
            ? internalLink(href, match[1], key)
            : externalLink(href, match[1], key),
        );
      } else if (match[3]) {
        nodes.push(
          <strong key={key} className="font-semibold">
            {match[3]}
          </strong>,
        );
      } else if (match[4]) {
        const href = trimTrailing(match[4]);
        consumed = href.length;
        nodes.push(externalLink(href, href.replace(/^https?:\/\//, ""), key));
      } else if (match[5]) {
        const href = trimTrailing(match[5]);
        consumed = href.length;
        nodes.push(internalLink(href, href, key));
      } else if (match[6]) {
        nodes.push(externalLink(`mailto:${match[6]}`, match[6], key));
      }

      last = match.index + consumed;
      PATTERN.lastIndex = last;
      i++;
    }
    if (last < text.length) nodes.push(text.slice(last));
    return nodes;
  };

  const blocks = content.split(/\n{2,}/).filter((b) => b.trim().length > 0);

  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        const isList = lines.every((l) => /^\s*[-*•]\s+/.test(l));
        if (isList) {
          return (
            <ul key={bi} className="list-disc space-y-1 pl-4">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^\s*[-*•]\s+/, ""), `${bi}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        return <p key={bi}>{renderInline(block.replace(/\n/g, " "), String(bi))}</p>;
      })}
    </div>
  );
};

export default MikrobiMessage;
